import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, Settings, LogOut, Trash2, ExternalLink, Box, Hexagon, CornerDownRight, MapPin, Star, Truck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHAPE_ICONS = {
  rectangular: Box,
  corner: CornerDownRight,
  hexagonal: Hexagon,
};

// Full 32 wood stain colors (matching ConfiguratorPage)
const WOOD_STAINS = {
  // PINE RANGE
  antique_stripped_pine: { name: "Antique Stripped Pine", hex: "#E8D4A8" },
  golden_pine: { name: "Golden Pine", hex: "#D4A84B" },
  dark_pine: { name: "Dark Pine", hex: "#8B6914" },
  // MAHOGANY RANGE
  brown_mahogany: { name: "Brown Mahogany", hex: "#5C3317" },
  very_red_mahogany: { name: "Very Red Mahogany", hex: "#8B2500" },
  dark_red_mahogany: { name: "Dark Red Mahogany", hex: "#5C1A0B" },
  // OAK RANGE
  light_oak: { name: "Light Oak", hex: "#C9A86C" },
  golden_oak: { name: "Golden Oak", hex: "#B8860B" },
  medium_oak: { name: "Medium Oak", hex: "#A0785A" },
  french_oak: { name: "French Oak", hex: "#8B7355" },
  english_oak: { name: "English Oak", hex: "#6B4423" },
  // WALNUT & ROSEWOOD
  dark_walnut: { name: "Dark Walnut", hex: "#4A3728" },
  light_rosewood: { name: "Light Rosewood", hex: "#9C6B5C" },
  light_sycamore: { name: "Light Sycamore", hex: "#F5DEB3" },
  // BLACK RANGE
  black_black: { name: "Black Black", hex: "#1A1A1A" },
  black_ebony: { name: "Black Ebony", hex: "#2D2D2D" },
  // GREY RANGE
  ironstone_grey: { name: "Ironstone Grey", hex: "#4A4A4A" },
  driftwood_grey: { name: "Driftwood Grey", hex: "#78736E" },
  moonlight_grey: { name: "Moonlight Grey", hex: "#B0B0B0" },
  dovetail_grey: { name: "Dovetail Grey", hex: "#6E6E6E" },
  // WHITE & SPECIAL
  soft_white: { name: "Soft White", hex: "#F5F5F0" },
  police_box_blue: { name: "Police Box Blue", hex: "#003B6F" },
  christmas_green: { name: "Christmas Green", hex: "#165B33" },
  christmas_red: { name: "Christmas Red", hex: "#B31B1B" },
  // NATURAL
  natural: { name: "Natural (Unfinished)", hex: "#E3C28D" },
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, configsRes, addressesRes] = await Promise.all([
        axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/configurations`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/addresses`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setOrders(ordersRes.data);
      setSavedConfigs(configsRes.data);
      setSavedAddresses(addressesRes.data);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfig = async (configId) => {
    try {
      await axios.delete(`${API}/configurations/${configId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedConfigs(prev => prev.filter(c => c.id !== configId));
      toast.success("Configuration deleted");
    } catch (error) {
      toast.error("Failed to delete configuration");
    }
  };

  const handleDeleteAddress = async (addressId) => {
    try {
      await axios.delete(`${API}/addresses/${addressId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedAddresses(prev => prev.filter(a => a.id !== addressId));
      toast.success("Address deleted");
    } catch (error) {
      toast.error("Failed to delete address");
    }
  };

  const handleSetDefaultAddress = async (addressId) => {
    try {
      await axios.put(`${API}/addresses/${addressId}/default`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedAddresses(prev => prev.map(a => ({
        ...a,
        is_default: a.id === addressId
      })));
      toast.success("Default address updated");
    } catch (error) {
      toast.error("Failed to update default address");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (!user) return null;

  if (loading) {
    return (
      <div className="min-h-screen pt-24 px-4 bg-[#0A0A0A] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 px-4 bg-[#0A0A0A]">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold font-['Space_Grotesk'] text-[#F4F1DE]">
              Welcome, {user.name}
            </h1>
            <p className="text-[#F4F1DE]/60">{user.email}</p>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="border-white/20 text-[#F4F1DE]/80 hover:border-red-500/50 hover:text-red-500"
            data-testid="dashboard-logout"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="orders" className="space-y-6">
          <TabsList className="bg-[#121212] border border-white/10">
            <TabsTrigger value="orders" className="data-[state=active]:bg-[#E07A5F] data-[state=active]:text-white">
              <Package className="w-4 h-4 mr-2" />
              Orders
            </TabsTrigger>
            <TabsTrigger value="configurations" className="data-[state=active]:bg-[#E07A5F] data-[state=active]:text-white">
              <Settings className="w-4 h-4 mr-2" />
              Saved Designs
            </TabsTrigger>
            <TabsTrigger value="addresses" className="data-[state=active]:bg-[#E07A5F] data-[state=active]:text-white">
              <MapPin className="w-4 h-4 mr-2" />
              Addresses
            </TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="orders" data-testid="orders-tab">
            {orders.length === 0 ? (
              <div className="text-center py-12 bg-[#121212] border border-white/10">
                <Package className="w-12 h-12 text-[#F4F1DE]/20 mx-auto mb-4" />
                <p className="text-[#F4F1DE]/60 mb-4">No orders yet</p>
                <Link to="/configurator">
                  <Button className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white">
                    Start Building
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div
                    key={order.id}
                    className="bg-[#121212] border border-white/10 p-6"
                    data-testid={`order-${order.id}`}
                  >
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-bold text-[#F4F1DE]">Order #{order.id.slice(0, 8)}</h3>
                          <span className={`px-2 py-1 text-xs rounded ${
                            order.status === 'delivered' 
                              ? 'bg-green-500/20 text-green-400'
                              : order.status === 'shipped'
                              ? 'bg-purple-500/20 text-purple-400'
                              : order.status === 'processing'
                              ? 'bg-blue-500/20 text-blue-400'
                              : order.status === 'paid'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {order.status.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-[#F4F1DE]/60 text-sm">
                          {new Date(order.created_at).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </p>
                        <p className="text-[#F4F1DE]/40 text-sm">
                          {order.items.length} item{order.items.length > 1 ? 's' : ''}
                        </p>
                        {/* Tracking Info */}
                        {order.tracking_number && (
                          <div className="mt-3 pt-3 border-t border-white/10">
                            <p className="text-[#F4F1DE]/60 text-sm flex items-center gap-2">
                              <Truck className="w-4 h-4 text-purple-400" />
                              <span className="font-mono">{order.tracking_number}</span>
                              {order.courier && (
                                <span className="text-[#F4F1DE]/40">via {order.courier}</span>
                              )}
                            </p>
                            {order.courier && (
                              <a
                                href={
                                  order.courier.toLowerCase() === 'dpd'
                                    ? `https://www.dpd.co.uk/tracking/trackingDetails?parcelCode=${order.tracking_number}`
                                    : order.courier.toLowerCase() === 'parcelforce'
                                    ? `https://www.parcelforce.com/track-trace?trackNumber=${order.tracking_number}`
                                    : '#'
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-[#E07A5F] text-sm mt-1 hover:underline"
                              >
                                Track Delivery
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                      <p className="text-2xl font-bold text-[#E07A5F]">
                        £{order.total.toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Saved Configurations Tab */}
          <TabsContent value="configurations" data-testid="configurations-tab">
            {savedConfigs.length === 0 ? (
              <div className="text-center py-12 bg-[#121212] border border-white/10">
                <Settings className="w-12 h-12 text-[#F4F1DE]/20 mx-auto mb-4" />
                <p className="text-[#F4F1DE]/60 mb-4">No saved configurations</p>
                <Link to="/configurator">
                  <Button className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white">
                    Design Your Enclosure
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {savedConfigs.map((config) => {
                  const ShapeIcon = SHAPE_ICONS[config.config.shape] || Box;
                  return (
                    <div
                      key={config.id}
                      className="bg-[#121212] border border-white/10 p-6 group hover:border-[#E07A5F]/50 transition-colors"
                      data-testid={`saved-config-${config.id}`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                          <ShapeIcon className="w-6 h-6 text-[#E07A5F]" />
                          <h3 className="font-bold text-[#F4F1DE]">{config.name}</h3>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteConfig(config.id)}
                            className="text-[#F4F1DE]/40 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 mb-3">
                        <span
                          className="w-5 h-5 rounded-full border border-white/20"
                          style={{ backgroundColor: WOOD_STAINS[config.config.stain_color]?.hex }}
                        />
                        <span className="text-[#F4F1DE]/60 text-sm">
                          {WOOD_STAINS[config.config.stain_color]?.name}
                        </span>
                      </div>
                      
                      <p className="text-[#F4F1DE]/60 text-sm mb-4">
                        {config.config.width}ft × {config.config.height}ft × {config.config.depth}ft
                      </p>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-bold text-[#E07A5F]">
                          £{config.price.toFixed(2)}
                        </span>
                        <Link to="/configurator">
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-white/20 text-[#F4F1DE]/80 hover:border-[#E07A5F]/50"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Open
                          </Button>
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Addresses Tab */}
          <TabsContent value="addresses" data-testid="addresses-tab">
            {savedAddresses.length === 0 ? (
              <div className="text-center py-12 bg-[#121212] border border-white/10">
                <MapPin className="w-12 h-12 text-[#F4F1DE]/20 mx-auto mb-4" />
                <p className="text-[#F4F1DE]/60 mb-4">No saved addresses</p>
                <p className="text-[#F4F1DE]/40 text-sm">
                  Addresses will be saved when you complete checkout
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {savedAddresses.map((address) => (
                  <div
                    key={address.id}
                    className={`bg-[#121212] border p-6 ${
                      address.is_default ? 'border-[#E07A5F]/50' : 'border-white/10'
                    }`}
                    data-testid={`address-${address.id}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-[#E07A5F]" />
                        <span className="font-bold text-[#F4F1DE]">{address.full_name}</span>
                      </div>
                      <div className="flex gap-2">
                        {!address.is_default && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSetDefaultAddress(address.id)}
                            className="text-[#F4F1DE]/40 hover:text-[#E07A5F]"
                            title="Set as default"
                          >
                            <Star className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteAddress(address.id)}
                          className="text-[#F4F1DE]/40 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="text-[#F4F1DE]/70 text-sm space-y-1">
                      <p>{address.address_line1}</p>
                      {address.address_line2 && <p>{address.address_line2}</p>}
                      <p>{address.city}{address.county && `, ${address.county}`}</p>
                      <p>{address.postcode}</p>
                      <p className="text-[#F4F1DE]/50">{address.phone}</p>
                    </div>
                    
                    {address.is_default && (
                      <span className="inline-flex items-center gap-1 mt-3 px-2 py-1 text-xs bg-[#E07A5F]/20 text-[#E07A5F] rounded">
                        <Star className="w-3 h-3 fill-current" />
                        Default Address
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DashboardPage;
